import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { AddRounded, CheckCircleRounded, ContentCopyOutlined, ErrorOutlineRounded, FileUploadOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { EmptyRow, Required, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PlainInput, StatutoryPage, SwitchLine, formatInr } from "pages/PayRoll/Shared/statutoryUi";
import {
  ActiveVersionCard,
  ComponentsTable,
  SettingsCard,
  SettingsGrid,
  VersionDetailsCard,
  VersionFooter,
  VersionStatusBanner,
  useVersionForm,
} from "pages/PayRoll/Shared/statutoryConfigUi";
import { PtVersionHistoryPanel } from "./PtVersionHistoryPanel";
import { PtSlabImport } from "./PtSlabImport";
import { LwfComponentPanel } from "pages/PayRoll/Configuration/StatutoryConfiguration/LWF/LwfComponentPanel";
import {
  ACTIVE_STATUS_COLORS,
  APPLIES_TO_SHORT,
  COLLECT_OPTIONS,
  EXEMPTION_MODE_OPTIONS,
  FREQUENCY_OPTIONS,
  HALF_YEAR_OPTIONS,
  MONTHS,
  MONTH_OPTIONS,
  REQUIRED_CODES,
  ROUNDING_OPTIONS,
  YEAR_START_OPTIONS,
  activeVersion,
  optionLabel,
  periodLabel,
  ruleProofLabel,
  slabCheck,
  sortSlabs,
  syncPeriodComponent,
  tableFormulaOf,
  validateSiteVersion,
  visibleComponents,
  yearlyMaximum,
} from "./ptConfigData";
import {
  checkPtFormulaOnServer,
  copyPtDraftFromSite,
  createPtDraftFrom,
  createPtVersion,
  discardPtDraft,
  fetchPtDefaults,
  fetchPtSite,
  fetchPtTestEmployees,
  savePtDraft,
  testMonthOf,
  testPtEmployee,
  updatePtVersion,
} from "./ptConfigApi";
import { PtSlabPanel } from "./PtSlabPanel";
import { PtExemptionPanel } from "./PtExemptionPanel";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";

// Site PT Configuration (PT1a): one site's PT rules with versions —
// version details, site and registration, when to deduct, slabs,
// exemptions and components. Built on the shared statutory version parts
// (same as ESI and LWF) and saved through the PT API (ptConfigApi.js); a
// draft on the server is auto-saved while it is edited.

const REQUIRED = new Set(REQUIRED_CODES);
const GROUP_ORDER = { ALL: 0, WOMEN: 1, MEN: 2 };

// Auto-save state of the draft: "", "saving", "saved" or "failed".
const AUTOSAVE_TEXT = {
  saving: "Saving the draft…",
  saved: "Draft saved. You can come back to it later.",
  failed: "The draft couldn't be saved automatically. Your changes are still here.",
};

// Drop-down values from GET config/options/, else the local lists.
const pick = (options, key, fallback) => (options?.[key]?.length ? options[key] : fallback);

// ---------------- Slabs and exemptions tables ----------------

const CheckLine = ({ check }) => (
  <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
    {check.valid ? <CheckCircleRounded sx={{ fontSize: 16, color: "#12B76A" }} /> : <ErrorOutlineRounded sx={{ fontSize: 16, color: "#DC2626" }} />}
    <Mui.Typography sx={{ ...designType.captionText, color: check.valid ? "#027A48" : "#DC2626" }}>{check.message}</Mui.Typography>
  </Mui.Stack>
);

const SlabsCard = ({ form, locked, onAdd, onImport, onOpen }) => {
  const specialOn = form.special_month_on;
  const rows = [...form.slabs].sort((a, b) => GROUP_ORDER[a.applies_to] - GROUP_ORDER[b.applies_to] || Number(a.wage_from) - Number(b.wage_from));
  const basis =
    form.frequency === "HALF_YEARLY"
      ? `Based on: 6-month PT wage (${periodLabel("HALF_YEARLY", form.period_start_months).replace(" · ", " or ") || "each half-year"})`
      : form.frequency === "YEARLY"
        ? "Based on: 12-month PT wage"
        : "Based on: the month's PT wage";
  const heads = [
    { label: "#", width: 40 },
    { label: "Wage from (₹)", width: 120 },
    { label: "Wage to (₹)", width: 120 },
    { label: "PT amount (₹)", width: 110 },
    ...(specialOn ? [{ label: `Amount in ${MONTHS[Number(form.special_month) - 1]?.slice(0, 3) || "special month"} (₹)`, width: 130 }] : []),
    { label: "Applies to", width: 100 },
    { label: "Actions", width: 110 },
  ];
  return (
    <SettingsCard
      title={`PT Slabs (${rows.length})`}
      info={`${basis} · enter as published by your local body`}
      action={
        <Mui.Stack direction="row" spacing={1}>
          <SecondaryButton startIcon={<FileUploadOutlined />} onClick={onImport} disabled={locked} sx={{ whiteSpace: "nowrap" }}>
            Import slabs
          </SecondaryButton>
          <SecondaryButton startIcon={<AddRounded />} onClick={onAdd} disabled={locked} sx={{ whiteSpace: "nowrap" }}>
            Add Slab
          </SecondaryButton>
        </Mui.Stack>
      }
    >
      <SimpleTable fit heads={heads}>
        {rows.length ? (
          rows.map((row, index) => (
            <Mui.TableRow key={row.id} hover>
              <Mui.TableCell sx={tableCellSx}>{index + 1}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{row.wage_from === "" ? "–" : formatInr(row.wage_from)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: row.wage_to === "" ? designColors.slate600 : undefined }}>{row.wage_to === "" ? "and above" : formatInr(row.wage_to)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.amount === "" ? "–" : formatInr(row.amount)}</Mui.TableCell>
              {specialOn ? <Mui.TableCell sx={tableCellSx}>{row.special_month_amount === "" ? "–" : formatInr(row.special_month_amount)}</Mui.TableCell> : null}
              <Mui.TableCell sx={tableCellSx}>{APPLIES_TO_SHORT[row.applies_to]}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Stack direction="row" spacing={0.5} alignItems="center">
                  <SecondaryButton onClick={() => onOpen(row)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                    {locked ? "View" : "Edit"}
                  </SecondaryButton>
                </Mui.Stack>
              </Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={heads.length} text="No slabs yet." />
        )}
      </SimpleTable>
      <CheckLine check={slabCheck(form.slabs, specialOn)} />
    </SettingsCard>
  );
};

const ExemptionsCard = ({ form, locked, showProof, modeOptions, onAdd, onOpen }) => {
  const heads = [
    { label: "#", width: 40 },
    { label: "Exemption reason", width: 220 },
    { label: "How it is applied", width: 200 },
    { label: "Rule / proof", width: 130 },
    ...(showProof ? [{ label: "Proof needed", width: 100 }] : []),
    { label: "Status", width: 90 },
    { label: "Actions", width: 110 },
  ];
  return (
    <SettingsCard
      title={`PT Exemptions (${form.exemptions.length})`}
      info="Who does not pay PT at this site · enter as per your state or local body rules · HR can only choose these reasons"
      action={
        <SecondaryButton startIcon={<AddRounded />} onClick={onAdd} disabled={locked} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          Add Exemption
        </SecondaryButton>
      }
    >
      <SimpleTable fit heads={heads}>
        {form.exemptions.length ? (
          form.exemptions.map((row, index) => (
            <Mui.TableRow key={row.id} hover sx={{ opacity: row.status === "Active" ? 1 : 0.6 }}>
              <Mui.TableCell sx={tableCellSx}>{index + 1}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.reason}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{optionLabel(modeOptions, row.mode)}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{ruleProofLabel(row)}</Mui.TableCell>
              {showProof ? <Mui.TableCell sx={tableCellSx}>{row.proof_required ? "Yes" : "No"}</Mui.TableCell> : null}
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={row.status} colorCode={ACTIVE_STATUS_COLORS[row.status]} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Stack direction="row" spacing={0.5} alignItems="center">
                  <SecondaryButton onClick={() => onOpen(row)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                    {locked ? "View" : "Edit"}
                  </SecondaryButton>
                </Mui.Stack>
              </Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={heads.length} text="No exemptions — everyone at this site pays PT." />
        )}
      </SimpleTable>
    </SettingsCard>
  );
};

// ---------------- Version form ----------------

const SiteVersionForm = ({ domain, companyId, site, versions, setVersions, selectedId, setSelectedId, hasSaved, options, persist }) => {
  const autosaveTimer = useRef(null);
  const autosaveRequest = useRef(null);
  const stopAutosave = () => {
    clearTimeout(autosaveTimer.current);
    autosaveRequest.current?.abort();
  };
  const version = useVersionForm({
    versions,
    setVersions,
    selectedId,
    setSelectedId,
    validate: validateSiteVersion,
    persist: {
      save: (form, flags) => {
        stopAutosave();
        return persist.save(form, flags);
      },
      discard: (form) => {
        stopAutosave();
        return persist.discard(form);
      },
    },
  });
  const { form, set, setComponents, error, locked, isDraft, active, dirty } = version;
  const [slabPanel, setSlabPanel] = useState({ open: false, slab: null });
  const [exemptionPanel, setExemptionPanel] = useState({ open: false, exemption: null });
  const [componentPanel, setComponentPanel] = useState({ open: false, mode: "edit", component: null });
  const [importOpen, setImportOpen] = useState(false);
  const [autosave, setAutosave] = useState("");
  const [employees, setEmployees] = useState(null);

  // A draft on the server (not the unsaved set-up form) is saved 1.5 s
  // after the last change; only formats are checked there.
  const serverDraft = Boolean(form) && isDraft && typeof form.id === "number";
  const formText = form ? JSON.stringify(form) : "";
  useEffect(() => {
    if (!serverDraft || !dirty) return undefined;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      autosaveRequest.current?.abort();
      const request = new AbortController();
      autosaveRequest.current = request;
      setAutosave("saving");
      savePtDraft({ domain, companyId, siteId: site.id, form: JSON.parse(formText), signal: request.signal })
        .then(() => setAutosave("saved"))
        .catch((err) => {
          if (err.name !== "AbortError") setAutosave("failed");
        });
    }, 1500);
    return () => clearTimeout(autosaveTimer.current);
  }, [serverDraft, dirty, formText, domain, companyId, site.id]);
  useEffect(
    () => () => {
      clearTimeout(autosaveTimer.current);
      autosaveRequest.current?.abort();
    },
    [],
  );
  const formId = form?.id;
  useEffect(() => setAutosave(""), [formId]);

  // Employees for "Test on employee" (PT employees list, this site first),
  // loaded the first time a slab or component drawer opens.
  const testOpen = slabPanel.open || componentPanel.open;
  useEffect(() => {
    if (!testOpen || employees) return undefined;
    const request = new AbortController();
    fetchPtTestEmployees({ domain, companyId, siteId: site.id, signal: request.signal })
      .then(setEmployees)
      .catch((err) => {
        if (err.name !== "AbortError") setEmployees([]);
      });
    return () => request.abort();
  }, [testOpen, employees, domain, companyId, site.id]);

  if (!form) return null;
  const periodic = form.frequency === "HALF_YEARLY" || form.frequency === "YEARLY";
  const limitWarning = Number(form.yearly_limit) > 0 && form.slabs.length && yearlyMaximum(form) > Number(form.yearly_limit);
  const collectOptions = pick(options, "collect_modes", COLLECT_OPTIONS);
  const collectHelp = collectOptions.find((option) => option.value === form.collect_mode)?.help;
  const showProof = Boolean(options?.attachments_enabled);

  const setFrequency = (frequency) => {
    set("frequency")(frequency);
    set("period_start_months")([]);
    if (frequency === "MONTHLY") set("collect_mode")("");
    setComponents((list) => syncPeriodComponent(frequency, list));
  };

  const saveList = (key) => (item) => {
    const list = form[key];
    set(key)(list.some((entry) => entry.id === item.id) ? list.map((entry) => (entry.id === item.id ? item : entry)) : [...list, item]);
  };
  const saveComponent = (component) => {
    setComponents((list) => {
      if (componentPanel.mode !== "add") return list.map((item) => (item.code === componentPanel.component.code ? component : item));
      // A new calculation component goes after the other calculation ones.
      const lastCalculation = list.map((item) => item.kind).lastIndexOf("CALCULATION");
      return [...list.slice(0, lastCalculation + 1), component, ...list.slice(lastCalculation + 1)];
    });
    setComponentPanel((current) => ({ ...current, open: false }));
  };

  const periodField =
    form.frequency === "HALF_YEARLY" ? (
      <SelectField
        value={form.period_start_months.join(",")}
        onChange={(value) => value && set("period_start_months")(value.split(","))}
        options={pick(options, "half_year_pairs", HALF_YEAR_OPTIONS)}
        disabled={locked}
        error={error("period_start_months")}
      />
    ) : form.frequency === "YEARLY" ? (
      <SelectField
        value={form.period_start_months[0] || ""}
        onChange={(value) => value && set("period_start_months")([value])}
        options={pick(options, "yearly_start_months", YEAR_START_OPTIONS)}
        disabled={locked}
        error={error("period_start_months")}
      />
    ) : (
      <PlainInput value="" placeholder={form.frequency ? "Not used — every month" : "Pick how often first"} onChange={() => {}} disabled />
    );

  const banner =
    isDraft && (!hasSaved || form.copied_from) ? (
      <RequestInfoBanner
        tone="purple"
        title={form.copied_from ? `Draft — copied from ${form.copied_from}. Not used by payroll until saved.` : "Draft — not used by payroll until saved."}
        body={
          form.copied_from
            ? "Enter this site's PT registration number and check the slabs and exemptions, then save."
            : "Fill in this site's PT rules and pick the month they start. Payroll deducts no PT here until you save; employees at this site are listed in Validation."
        }
      />
    ) : (
      <VersionStatusBanner form={form} active={active} locked={locked} isDraft={isDraft} itemLabel="PT" onOpenActive={() => setSelectedId(active.id)} />
    );

  return (
    <Mui.Stack spacing={2}>
      {banner}
      <VersionDetailsCard form={form} locked={locked} isDraft={isDraft} set={set} error={error} />

      <SettingsGrid columns={{ xs: "1fr", lg: "1fr 1fr" }}>
        <SettingsCard title="Site and registration" info="Employees working at this site on the last day of the month pay PT here">
          <SettingsGrid columns={{ xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }}>
            {/* The site name is in the page title, so it isn't repeated here. */}
            <Field label={<>State<Required /></>} error={error("state")}>
              <PlainInput value={form.state || site.state} placeholder="From the site" onChange={() => {}} disabled error={error("state")} />
            </Field>
            <Field label="Local body (optional)">
              <PlainInput value={form.local_body || site.local_body || ""} placeholder="From the site" onChange={() => {}} disabled />
            </Field>
            <Field label={<>PT registration no.<Required /></>} error={error("registration_no")}>
              <PlainInput
                value={form.registration_no}
                onChange={set("registration_no")}
                disabled={locked}
                error={error("registration_no")}
                placeholder="e.g. TN/PT/CHN/0045"
              />
            </Field>
            <Field label={<>How often PT is deducted<Required /></>} error={error("frequency")}>
              <SelectField
                value={form.frequency}
                onChange={(value) => value && setFrequency(value)}
                options={pick(options, "frequencies", FREQUENCY_OPTIONS)}
                disabled={locked}
                error={error("frequency")}
              />
            </Field>
            <Field label={<>{form.frequency === "YEARLY" ? "Year" : "Half-years"}{periodic ? <Required /> : null}</>} error={error("period_start_months")}>
              {periodField}
            </Field>
          </SettingsGrid>
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner body="Monthly: slab is picked on that month's PT wage. Half-yearly: Cauver adds up the PT wage of the 6 months (Apr–Sep or Oct–Mar) and picks the slab on that total. Yearly: the same on the 12-month total. State and local body come from Administration › Sites." />
          </Mui.Box>
        </SettingsCard>

        <SettingsCard title="When to deduct" info="For half-yearly and yearly sites">
          <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }}>
            <Field label={<>How to collect{periodic ? <Required /> : null}</>} error={error("collect_mode")}>
              {periodic ? (
                <SelectField value={form.collect_mode} onChange={(value) => value && set("collect_mode")(value)} options={collectOptions} disabled={locked} error={error("collect_mode")} />
              ) : (
                <PlainInput value="" placeholder={form.frequency === "MONTHLY" ? "Every month (monthly site)" : "Pick how often first"} onChange={() => {}} disabled />
              )}
            </Field>
            <Field label={<>Payment due (days)<Required /></>} error={error("payment_due_days")}>
              <PlainInput
                type="number"
                value={form.payment_due_days}
                onChange={set("payment_due_days")}
                disabled={locked}
                error={error("payment_due_days")}
                placeholder="e.g. 30"
                inputProps={{ min: 0, max: 365 }}
              />
              {/* Kept under the box so the label stays one line and both boxes line up. */}
              <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
                Days after the {form.frequency === "MONTHLY" ? "month" : form.frequency === "YEARLY" ? "year" : "half-year"} ends
              </Mui.Typography>
            </Field>
          </SettingsGrid>
          {periodic ? (
            <Mui.Box sx={{ mt: 2 }}>
              <RequestInfoBanner
                body={
                  collectHelp ||
                  "Options: Once, in the last month (full amount in the last month of the period) or Every month, balance in the last month (an estimate each month; the last month settles the difference)."
                }
              />
            </Mui.Box>
          ) : null}
          <Mui.Box sx={{ mt: 1 }}>
            <SwitchLine checked={form.settle_leavers} onChange={set("settle_leavers")} disabled={locked} label="Employee leaving mid period: settle PT in their last payroll" />
            <SwitchLine
              checked={form.special_month_on}
              onChange={(checked) => {
                set("special_month_on")(checked);
                if (!checked) set("special_month")("");
              }}
              disabled={locked}
              label="Different amount in one month (for example February) — used by some monthly states"
            >
              {form.special_month_on ? (
                <Mui.Box sx={{ width: 160 }}>
                  <SelectField
                    value={form.special_month}
                    onChange={(value) => value && set("special_month")(value)}
                    options={pick(options, "months", MONTH_OPTIONS)}
                    disabled={locked}
                    error={error("special_month")}
                  />
                </Mui.Box>
              ) : null}
            </SwitchLine>
          </Mui.Box>
          <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }} sx={{ mt: 1.5 }}>
            <Field label={<>Most PT per employee in a year (₹)<Required /></>} error={error("yearly_limit")}>
              <PlainInput type="number" adornment="₹" value={form.yearly_limit} onChange={set("yearly_limit")} disabled={locked} error={error("yearly_limit")} placeholder="e.g. 2,500" inputProps={{ min: 1 }} />
            </Field>
            <Field label="Rounding">
              <SelectField value={form.rounding} onChange={(value) => value && set("rounding")(value)} options={pick(options, "rounding", ROUNDING_OPTIONS)} disabled={locked} />
            </Field>
          </SettingsGrid>
          <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>
            Legal yearly limit. Cauver never deducts more than this in a financial year (Apr–Mar) and shows a warning if the slabs would go above it.
          </Mui.Typography>
          {limitWarning ? (
            <Mui.Box sx={{ mt: 1.5 }}>
              <RequestInfoBanner
                tone="warning"
                body={`The top slab can reach ₹${formatInr(yearlyMaximum(form))} a year, above the ₹${formatInr(form.yearly_limit)} limit. Cauver caps each employee at the limit; check the slabs if this is not expected.`}
              />
            </Mui.Box>
          ) : null}
        </SettingsCard>
      </SettingsGrid>

      <SlabsCard
        form={form}
        locked={locked}
        onAdd={() => setSlabPanel({ open: true, slab: null })}
        onImport={() => setImportOpen(true)}
        onOpen={(slab) => setSlabPanel({ open: true, slab })}
      />
      {/* The table's check line shows the local slab check; this is the server's. */}
      {error("slabs") && slabCheck(form.slabs, form.special_month_on).valid ? <RequestInfoBanner tone="danger" body={error("slabs")} /> : null}
      <ExemptionsCard
        form={form}
        locked={locked}
        showProof={showProof}
        modeOptions={pick(options, "exemption_modes", EXEMPTION_MODE_OPTIONS)}
        onAdd={() => setExemptionPanel({ open: true, exemption: null })}
        onOpen={(exemption) => setExemptionPanel({ open: true, exemption })}
      />
      {error("exemptions") ? <RequestInfoBanner tone="danger" body={error("exemptions")} /> : null}

      {["CALCULATION", "CONTRIBUTION"].map((kind) => (
        <ComponentsTable
          key={kind}
          title={kind === "CALCULATION" ? "Calculation Components" : "Contribution Components"}
          info={kind === "CALCULATION" ? "Wage PT is worked out on" : "PT deducted from the employee"}
          kind={kind}
          components={visibleComponents(form)}
          locked={locked}
          requiredCodes={REQUIRED}
          formulaOf={tableFormulaOf}
          versionName={form.name}
          onAdd={kind === "CALCULATION" ? () => setComponentPanel({ open: true, mode: "add", component: null }) : undefined}
          onOpen={(component) => setComponentPanel({ open: true, mode: "edit", component })}
          onChange={setComponents}
          showMenu={false}
        />
      ))}
      {error("components") ? <RequestInfoBanner tone="danger" body={error("components")} /> : null}

      {serverDraft && autosave ? (
        <Mui.Typography sx={{ ...designType.captionText, textAlign: "right", color: autosave === "failed" ? "#DC2626" : designColors.slate600 }}>
          {AUTOSAVE_TEXT[autosave]}
        </Mui.Typography>
      ) : null}
      <VersionFooter
        form={form}
        active={active}
        locked={locked}
        isDraft={isDraft}
        dirty={dirty}
        saving={version.saving}
        onCancel={version.reset}
        onSave={version.save}
        onDiscard={version.discard}
      />

      <PtSlabPanel
        open={slabPanel.open}
        slab={slabPanel.slab}
        form={form}
        siteName={site.name}
        readOnly={locked}
        domain={domain}
        companyId={companyId}
        siteId={site.id}
        employees={employees}
        appliesToOptions={options?.applies_to}
        onClose={() => setSlabPanel((current) => ({ ...current, open: false }))}
        onSave={(slab) => {
          saveList("slabs")(slab);
          setSlabPanel((current) => ({ ...current, open: false }));
        }}
      />
      <PtExemptionPanel
        open={exemptionPanel.open}
        exemption={exemptionPanel.exemption}
        form={form}
        siteName={site.name}
        readOnly={locked}
        showProof={showProof}
        modeOptions={pick(options, "exemption_modes", EXEMPTION_MODE_OPTIONS)}
        onClose={() => setExemptionPanel((current) => ({ ...current, open: false }))}
        onSave={(exemption) => {
          saveList("exemptions")(exemption);
          setExemptionPanel((current) => ({ ...current, open: false }));
        }}
      />
      <LwfComponentPanel
        open={componentPanel.open}
        mode={componentPanel.mode}
        component={componentPanel.component}
        form={form}
        siteName={site.name}
        readOnly={locked}
        requiredCodes={REQUIRED_CODES}
        itemLabel="PT"
        fixedNote="Fixed by PT: the period wage adds up PT_WAGE over the period, and PT comes from the slab that total falls in. Change the slabs to change the amount."
        server={{
          domain,
          companyId,
          siteId: site.id,
          employees,
          checkFormula: checkPtFormulaOnServer,
          testEmployee: testPtEmployee,
          month: testMonthOf(form),
          wageCode: "PT_WAGE",
        }}
        onClose={() => setComponentPanel((current) => ({ ...current, open: false }))}
        onSave={saveComponent}
      />
      <PtSlabImport
        open={importOpen}
        form={form}
        siteName={site.name}
        domain={domain}
        companyId={companyId}
        onClose={() => setImportOpen(false)}
        onImport={(slabs) => {
          set("slabs")(sortSlabs(slabs));
          showToast("success", `${slabs.length} slabs imported. Save the version to keep them.`);
        }}
      />
    </Mui.Stack>
  );
};

// ---------------- Page ----------------

export const PtSiteConfig = ({ domain, companyId, siteId, siteRows, options, onBack, onSitesChanged }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const [site, setSite] = useState(() => {
    const row = siteRows.find((item) => item.id === siteId);
    return { id: siteId, name: row?.site || "", code: row?.code || "", state: row?.state || "", local_body: row?.local_body || "" };
  });
  const [versions, setVersions] = useState([]);
  const [load, setLoad] = useState({ loading: true, error: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFromId, setCopyFromId] = useState("");
  const controller = useRef(null);

  // (Re)load the site's versions and draft. Keeps the selection if it still
  // exists, else `selectId` (the version just saved) or the active version.
  // A site with nothing saved and no draft opens the blank set-up form.
  const loadSite = useCallback(
    async (selectId) => {
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      setLoad((state) => ({ ...state, loading: true, error: "" }));
      try {
        const data = await fetchPtSite({ domain, companyId, siteId, signal: request.signal });
        let list = data.versions;
        if (!list.length) list = [await fetchPtDefaults({ domain, companyId, siteId, signal: request.signal })];
        setSite((current) => ({ ...current, ...Object.fromEntries(Object.entries(data.site).filter(([, value]) => value)) }));
        setVersions(list);
        setSelectedId((currentId) => {
          const wanted = selectId ?? currentId;
          if (list.some((version) => version.id === wanted)) return wanted;
          return activeVersion(list.filter((version) => version.status !== "Draft"))?.id ?? list[list.length - 1]?.id ?? null;
        });
        setLoad({ loading: false, error: "" });
      } catch (err) {
        if (err.name === "AbortError") return;
        setLoad({ loading: false, error: err.message || "Couldn't load this site's PT configuration." });
      }
    },
    [domain, companyId, siteId],
  );

  useEffect(() => {
    if (companyId) loadSite();
    return () => controller.current?.abort();
  }, [loadSite, companyId]);

  const saved = versions.filter((version) => version.status !== "Draft");
  const hasSaved = saved.length > 0;
  const active = activeVersion(saved);
  const draft = versions.find((version) => version.status === "Draft");
  const copySources = siteRows.filter((row) => row.set_up && row.id !== siteId);

  // Create New Version copies the active version into the site's draft;
  // when a draft already exists it is opened instead.
  const createVersion = async () => {
    if (draft) {
      setSelectedId(draft.id);
      return;
    }
    // The new draft starts after the latest saved version, so it copies that
    // one (a scheduled version's changes carry on), not only the active one.
    const latest = [...saved].sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from))).pop();
    const base = latest || active;
    if (!base || busy) return;
    setBusy(true);
    try {
      const { version, message } = await createPtDraftFrom({ domain, companyId, siteId, versionId: base.id });
      showToast("success", message || `${version.name} created as a draft.`);
      setVersions((list) => [...list.filter((item) => item.status !== "Draft" && item.id !== "new"), version]);
      setSelectedId(version.id);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyFromSite = async () => {
    const source = copySources.find((row) => String(row.id) === copyFromId);
    if (!source || busy) return;
    setBusy(true);
    try {
      const { version, message } = await copyPtDraftFromSite({ domain, companyId, siteId, sourceSiteId: source.id });
      setCopyOpen(false);
      showToast("success", message || `Rules copied from ${source.site} into a draft. Not used by payroll until saved.`);
      setVersions((list) => [...list.filter((item) => item.status !== "Draft" && item.id !== "new"), version]);
      setSelectedId(version.id);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setBusy(false);
    }
  };

  // Save (draft or set-up form -> new version) or Save changes (saved,
  // unlocked version). The server's warnings show after the toast; they
  // never block the save. A 409 means the version changed or got locked:
  // reload so the screen shows it.
  const persist = {
    save: async (form, { isDraft }) => {
      try {
        const result = isDraft ? await createPtVersion({ domain, companyId, siteId, form }) : await updatePtVersion({ domain, companyId, form });
        if (isDraft && form.id !== "new") await discardPtDraft({ domain, companyId, siteId }).catch(() => {});
        showToast("success", result.message || `${result.version.name} saved.`);
        result.warnings.forEach((warning) => showToast("info", warning));
        await loadSite(result.version.id);
        onSitesChanged();
      } catch (err) {
        if (err.status === 409) {
          if (err.code === "VERSION_LOCKED") err.message = `${err.message || "This version is locked."} Use Create New Version to change it.`;
          loadSite(form.id);
        }
        throw err;
      }
    },
    discard: async (form) => {
      if (form.id !== "new") await discardPtDraft({ domain, companyId, siteId });
      showToast("success", "Draft discarded.");
      if (hasSaved) await loadSite(active?.id);
      else onBack();
    },
  };

  const copyButton = (
    <SecondaryButton
      startIcon={<ContentCopyOutlined />}
      onClick={() => {
        setCopyFromId(copySources[0] ? String(copySources[0].id) : "");
        setCopyOpen(true);
      }}
      disabled={!copySources.length || busy}
      sx={{ whiteSpace: "nowrap" }}
    >
      Copy from site
    </SecondaryButton>
  );
  const copySource = copySources.find((row) => String(row.id) === copyFromId);
  const title = site.name ? `${site.name} — PT Configuration` : "PT Configuration";

  return (
    <StatutoryPage
      breadcrumb={settingsCrumbs("Statutory", { label: "Professional Tax", onClick: onBack }, site.name || "Site")}
      title={title}
      subtitle="PT rule for this site. Create a new version when the slabs or rules change."
      onBack={onBack}
      aside={
        load.loading && !versions.length ? null : hasSaved ? (
          <ActiveVersionCard version={active} hasDraft={Boolean(draft)} onHistory={() => setHistoryOpen(true)} onCreate={createVersion} extra={copyButton} />
        ) : (
          copyButton
        )
      }
    >
      {load.error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load this site's PT configuration."
          body={load.error}
          action={
            <SecondaryButton onClick={() => loadSite()} sx={{ whiteSpace: "nowrap" }}>
              Retry
            </SecondaryButton>
          }
        />
      ) : load.loading && !versions.length ? (
        <Mui.Box role="status" aria-label="Loading the site's PT configuration">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : (
        <SiteVersionForm
          domain={domain}
          companyId={companyId}
          site={site}
          versions={versions}
          setVersions={setVersions}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          hasSaved={hasSaved}
          options={options}
          persist={persist}
        />
      )}

      <PtVersionHistoryPanel
        open={historyOpen}
        domain={domain}
        companyId={companyId}
        versionId={saved.some((version) => version.id === selectedId) ? selectedId : active?.id ?? null}
        draft={draft}
        subtitle={`${site.name} · Professional Tax`}
        onClose={() => setHistoryOpen(false)}
        onOpenVersion={(id) => {
          // A version the page has not loaded yet (e.g. saved elsewhere) reloads the site.
          if (versions.some((version) => String(version.id) === String(id))) setSelectedId(versions.find((version) => String(version.id) === String(id)).id);
          else loadSite(id);
          setHistoryOpen(false);
        }}
      />

      <GlobalConfirmDialog
        open={copyOpen}
        title="Copy from site"
        onCancel={() => setCopyOpen(false)}
        onConfirm={copyFromSite}
        confirmLabel={busy ? "Copying..." : "Copy"}
        cancelLabel="Cancel"
        confirmDisabled={!copyFromId || busy}
        maxWidth="sm"
      >
        <Mui.Stack spacing={2}>
          <Mui.Typography sx={designType.helperText}>
            Copies every setting of another site's saved version — slabs, exemptions and components included — into a draft for {site.name}
            {draft ? ` (replacing what is in ${draft.name})` : ""}. The registration number is left empty; state and local body stay this site's. Nothing is
            used by payroll until you save.
          </Mui.Typography>
          <Field label="Copy from">
            <SelectField
              value={copyFromId}
              onChange={setCopyFromId}
              options={copySources.map((row) => ({
                value: String(row.id),
                label: [row.site, row.version_label, row.frequency_label].filter(Boolean).join(" · "),
              }))}
              disablePortal={false}
            />
          </Field>
          {copySource ? (
            <Mui.Typography sx={designType.captionText}>{[copySource.state, copySource.local_body].filter(Boolean).join(" · ")}</Mui.Typography>
          ) : null}
        </Mui.Stack>
      </GlobalConfirmDialog>
    </StatutoryPage>
  );
};
